/**
 * SHINE Predictive Search
 * Handles dynamic search drawer opening/closing, debounced AJAX queries,
 * and Section Rendering API injection for instant product results.
 */
(function () {
  'use strict';

  const selectors = {
    drawer: '#search-drawer',
    container: '#search-drawer-container',
    input: '#search-drawer-input',
    closeBtn: '#search-drawer-close',
    loading: '#search-drawer-loading',
    suggestions: '#search-drawer-suggestions',
    target: '#predictive-search-target',
    toggles: '[data-search-toggle]',
    tags: '.search-drawer-tag'
  };

  let searchTimeout = null;
  let currentSearchQuery = '';

  // ─── Open Search Drawer ──────────────────────────────────
  function openSearch() {
    const drawer = document.querySelector(selectors.drawer);
    const container = document.querySelector(selectors.container);
    const input = document.querySelector(selectors.input);

    if (!drawer || !container) return;

    // Reset input and results on opening
    if (input) {
      input.value = '';
      currentSearchQuery = '';
    }
    
    // Show default suggestions, hide dynamic results
    const suggestions = document.querySelector(selectors.suggestions);
    const target = document.querySelector(selectors.target);
    const loading = document.querySelector(selectors.loading);
    const clearBtn = document.getElementById('search-drawer-clear');

    if (suggestions) suggestions.style.display = 'block';
    if (target) {
      target.style.display = 'none';
      target.innerHTML = '';
    }
    if (loading) loading.classList.add('hidden');
    if (clearBtn) clearBtn.classList.add('hidden');

    // Transitions
    drawer.classList.remove('pointer-events-none');
    drawer.classList.remove('opacity-0');
    drawer.classList.add('opacity-100');

    container.classList.remove('-translate-y-10', 'opacity-0');
    container.classList.add('translate-y-0', 'opacity-100');

    document.body.style.overflow = 'hidden';
    drawer.setAttribute('aria-hidden', 'false');

    // Focus input
    setTimeout(() => {
      if (input) input.focus();
    }, 150);
  }

  // ─── Close Search Drawer ─────────────────────────────────
  function closeSearch() {
    const drawer = document.querySelector(selectors.drawer);
    const container = document.querySelector(selectors.container);

    if (!drawer || !container) return;

    // Transitions
    drawer.classList.add('pointer-events-none');
    drawer.classList.remove('opacity-100');
    drawer.classList.add('opacity-0');

    container.classList.remove('translate-y-0', 'opacity-100');
    container.classList.add('-translate-y-10', 'opacity-0');

    document.body.style.overflow = '';
    drawer.setAttribute('aria-hidden', 'true');
  }

  // ─── Perform Predictive Search ───────────────────────────
  function performSearch(query) {
    const term = query.trim();
    const suggestions = document.querySelector(selectors.suggestions);
    const target = document.querySelector(selectors.target);
    const loading = document.querySelector(selectors.loading);

    if (term.length === 0) {
      if (suggestions) suggestions.style.display = 'block';
      if (target) {
        target.style.display = 'none';
        target.innerHTML = '';
      }
      if (loading) loading.classList.add('hidden');
      return;
    }

    if (term === currentSearchQuery) return;
    currentSearchQuery = term;

    // Show loading state, hide default lists and target
    if (suggestions) suggestions.style.display = 'none';
    if (target) target.style.display = 'none';
    if (loading) loading.classList.remove('hidden');

    // Fetch predictive search results section pre-rendered
    const searchUrl = `/search/suggest?q=${encodeURIComponent(term)}&resources[type]=product&section_id=predictive-search`;

    fetch(searchUrl)
      .then((res) => {
        if (!res.ok) throw new Error('Search request failed');
        return res.text();
      })
      .then((html) => {
        if (currentSearchQuery !== term) return; // Prevent race conditions

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const results = doc.getElementById('predictive-search-results');

        if (loading) loading.classList.add('hidden');

        if (target && results) {
          target.innerHTML = results.outerHTML;
          target.style.display = 'block';
          
          // Re-sync wishlist toggle states on the newly injected card items
          if (window.wishlist && typeof window.wishlist.updateUI === 'function') {
            window.wishlist.updateUI();
          }
        }
      })
      .catch((err) => {
        console.error('Predictive Search Error:', err);
        if (loading) loading.classList.add('hidden');
      });
  }

  // ─── Bind Events ─────────────────────────────────────────
  function init() {
    const drawer = document.querySelector(selectors.drawer);
    const closeBtn = document.querySelector(selectors.closeBtn);
    const input = document.querySelector(selectors.input);

    // Click on toggle links opens search drawer
    document.querySelectorAll(selectors.toggles).forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        openSearch();
      });
    });

    // Close on Close Button click
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        closeSearch();
      });
    }

    // Close on backdrop overlay click
    if (drawer) {
      drawer.addEventListener('click', (e) => {
        if (e.target === drawer) {
          closeSearch();
        }
      });
    }

    // Close on Escape key press
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        closeSearch();
      }
    });

    // Dynamic keystroke input debouncer
    if (input) {
      input.addEventListener('input', () => {
        const query = input.value;

        // Show/hide custom clear button dynamically
        const clearBtn = document.getElementById('search-drawer-clear');
        if (clearBtn) {
          if (query.trim().length > 0) {
            clearBtn.classList.remove('hidden');
          } else {
            clearBtn.classList.add('hidden');
          }
        }

        if (searchTimeout) clearTimeout(searchTimeout);

        searchTimeout = setTimeout(() => {
          performSearch(query);
        }, 300);
      });
    }

    // Tag pills click handler
    document.querySelectorAll(selectors.tags).forEach((tag) => {
      tag.addEventListener('click', (e) => {
        e.preventDefault();
        const text = tag.textContent.trim();
        if (input) {
          input.value = text;
          
          // Show custom clear button on tag click
          const clearBtn = document.getElementById('search-drawer-clear');
          if (clearBtn) clearBtn.classList.remove('hidden');

          input.focus();
          performSearch(text);
        }
      });
    });

    // Custom clear button click handler
    const clearBtn = document.getElementById('search-drawer-clear');
    if (clearBtn && input) {
      clearBtn.addEventListener('click', (e) => {
        e.preventDefault();
        input.value = '';
        clearBtn.classList.add('hidden');
        input.focus();
        performSearch('');
      });
    }
  }

  // Wait for DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
